import { useState, useRef, useCallback } from 'react';

interface Props {
  onSend: (content: string) => void;
  onTyping?: () => void;
  onTypingStop?: () => void;
  disabled?: boolean;
  placeholder?: string;
  topSlot?: React.ReactNode;
  allowEmpty?: boolean;
  leftControl?: React.ReactNode;
}

export function MessageComposer({
  onSend, onTyping, onTypingStop, disabled, placeholder, topSlot, allowEmpty, leftControl,
}: Props) {
  const [value, setValue] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  const send = useCallback(() => {
    const trimmed = value.trim();
    if (disabled) return;
    if (!trimmed && !allowEmpty) return;
    onSend(trimmed);
    onTypingStop?.();
    setValue('');
    if (ref.current) ref.current.style.height = 'auto';
  }, [value, disabled, allowEmpty, onSend, onTypingStop]);

  const canSend = !disabled && (value.trim().length > 0 || !!allowEmpty);

  return (
    <div className="border-t border-neutral-200 bg-white px-4 py-3 sm:px-6">
      {topSlot}
      <div className="flex items-end gap-1.5 rounded-2xl border border-neutral-200 bg-neutral-50 p-1.5 transition-all focus-within:border-brand-teal focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-teal/15">
        {leftControl}
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            onTyping?.();
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={placeholder ?? 'Scrivi un messaggio…'}
          disabled={disabled}
          rows={1}
          className="min-h-[36px] flex-1 resize-none border-0 bg-transparent px-2 py-1.5 text-[14.5px] text-neutral-900 placeholder-neutral-400 outline-none focus:ring-0 disabled:opacity-40"
        />
        <button
          onClick={send}
          disabled={!canSend}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all ${
            canSend
              ? 'bg-brand-teal text-white shadow-sm shadow-brand-teal/30 hover:bg-brand-teal-dark active:scale-95'
              : 'bg-neutral-200 text-neutral-400'
          }`}
          aria-label="Invia messaggio"
        >
          <svg className="h-[18px] w-[18px] translate-x-px" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
          </svg>
        </button>
      </div>
    </div>
  );
}
